import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHouse, faList, faBook, faComments, faSearch, faX } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useFamily } from '../../contexts/FamilyContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { sortExperiencesByDate, filterExperiencesByStatus } from '../../utils/experiences';
import { getExperienceType } from '../../data/experienceTypes';

const navItems = [
  { id: 'dashboard', icon: faHouse, label: 'Dashboard', path: '/' },
  { id: 'experiences', icon: faList, label: 'Experiences', path: '/experiences' },
  { id: 'explore', icon: faBook, label: 'Explore', path: '/explore' },
  { id: 'chats', icon: faComments, label: 'Chats', path: '/chats' },
  { id: 'search', icon: faSearch, label: 'Search', path: null }, // null path means it opens search palette
];

export default function MobileNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { familyId } = useFamily();
  const [showSearchPalette, setShowSearchPalette] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentExperiences, setRecentExperiences] = useState([]);

  // Load recent experiences for search
  useEffect(() => {
    if (!familyId) return;

    const q = query(
      collection(db, 'projects'),
      where('familyId', '==', familyId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const experiences = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const active = filterExperiencesByStatus(experiences, 'active');
      const recent = sortExperiencesByDate(active).slice(0, 5);
      setRecentExperiences(recent);
    });

    return () => unsubscribe();
  }, [familyId]);

  // Listen for openSearchPalette event
  useEffect(() => {
    const handleOpenSearch = () => {
      setShowSearchPalette(true);
    };

    window.addEventListener('openSearchPalette', handleOpenSearch);
    return () => window.removeEventListener('openSearchPalette', handleOpenSearch);
  }, []);

  // Don't show nav on login, onboarding, or public routes
  const hideNavPaths = ['/login', '/onboarding', '/accept-invite'];
  if (hideNavPaths.some(path => location.pathname.startsWith(path))) {
    return null;
  }

  // Determine active tab based on current path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/') return 'dashboard';
    if (path === '/experiences' || path.startsWith('/experience') || path.startsWith('/project')) return 'experiences';
    if (path.startsWith('/explore') || path.startsWith('/community')) return 'explore';
    if (path.startsWith('/chats') || path.startsWith('/chat')) return 'chats';
    return null; // No active tab for other pages
  };

  const activeTab = getActiveTab();

  const handleNavClick = (item) => {
    if (item.id === 'search') {
      // Open search palette
      setShowSearchPalette(true);
      return;
    }
    if (item.path) {
      navigate(item.path);
    }
  };

  const handleExperienceClick = (experienceId) => {
    navigate(`/experience/${experienceId}`);
    setShowSearchPalette(false);
  };

  const handleSearchNavClick = (item) => {
    handleNavClick(item);
    setShowSearchPalette(false);
  };

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-border z-40 md:hidden">
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`
                  relative flex items-center justify-center 
                  flex-1 h-full min-h-[44px]
                  transition-all duration-200
                  ${isActive 
                    ? 'text-primary' 
                    : 'text-text-secondary hover:text-text-primary'
                  }
                  active:scale-95
                `}
                aria-label={item.label}
              >
                <FontAwesomeIcon 
                  icon={item.icon} 
                  className="text-xl" 
                  size="lg"
                />
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            );
          })}
        </div>
        
        {/* Safe area for devices with home indicator */}
        <div className="h-safe-area-inset-bottom bg-white" />
      </nav>

      {/* Mobile Search Palette */}
      <AnimatePresence>
        {showSearchPalette && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowSearchPalette(false)}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl z-50 max-h-[85vh] flex flex-col md:hidden overflow-hidden safe-bottom"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Handle bar */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
              </div>

              {/* Search Input Section */}
              <div className="px-6 pb-4 border-b border-border">
                <div className="flex items-center gap-3 bg-surface rounded-xl px-4 py-3">
                  <FontAwesomeIcon icon={faSearch} className="w-5 h-5 text-text-secondary flex-shrink-0" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search pages, experiences..."
                    className="flex-1 text-base bg-transparent border-none outline-none text-text-primary placeholder:text-text-secondary/60"
                    autoFocus
                    style={{ fontSize: '16px' }} // Prevent iOS zoom
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="p-1 -mr-1 touch-target"
                      aria-label="Clear search"
                    >
                      <FontAwesomeIcon icon={faX} className="w-4 h-4 text-text-secondary" />
                    </button>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto momentum-scroll px-6 py-4">
                {/* Quick Navigation */}
                <div className="mb-4">
                  <div className="px-2 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
                    Quick Navigation
                  </div>
                  <div className="space-y-1">
                    {navItems.filter(item => item.id !== 'search').map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleSearchNavClick(item)}
                        className="w-full text-left px-4 py-3 rounded-xl hover:bg-surface active:bg-surface active:scale-[0.98] transition-all flex items-center gap-4 touch-target group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                          <FontAwesomeIcon icon={item.icon} className="w-5 h-5 text-primary" />
                        </div>
                        <span className="text-base font-medium text-text-primary flex-1">{item.label}</span>
                        <FontAwesomeIcon icon={faX} className="w-4 h-4 text-text-secondary rotate-45 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Recent Experiences */}
                {recentExperiences.length > 0 && (
                  <div className="mt-6">
                    <div className="px-2 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">
                      Recent Experiences
                    </div>
                    <div className="space-y-1">
                      {recentExperiences.map((experience) => {
                        const experienceType = getExperienceType(experience.type);
                        return (
                          <button
                            key={experience.id}
                            onClick={() => handleExperienceClick(experience.id)}
                            className="w-full text-left px-4 py-3 rounded-xl hover:bg-surface active:bg-surface active:scale-[0.98] transition-all touch-target group"
                          >
                            <div className="font-semibold text-base text-text-primary mb-0.5 group-hover:text-primary transition-colors">
                              {experience.title}
                            </div>
                            <div className="text-sm text-text-secondary">
                              {experienceType?.name || experience.type}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Empty state when no search results */}
                {searchQuery && (
                  <div className="text-center py-12">
                    <FontAwesomeIcon icon={faSearch} className="w-12 h-12 text-text-secondary/40 mb-4" />
                    <p className="text-text-secondary">No results found</p>
                    <p className="text-sm text-text-secondary/60 mt-1">Try a different search term</p>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

